import AppPagination from "@/component/common/AppPagination.tsx";
import ManageContentLayout from "@/component/common/layout/ManageContentLayout.tsx";
import ContentParagraph from "@/component/ContentParagraph.tsx";
import { StringHelper } from "@/constant/helper.ts";
import { ContactService } from "@/services/contact/ContactService.ts";
import { toast } from "@/utils/toast.tsx";
import { ExportHook, PopconfirmHook } from "@/view/components";
import { VerticalAlignBottomOutlined } from "@ant-design/icons";
import {
    Button,
    Descriptions,
    Flex,
    Modal,
    Space,
    Table,
    TablePaginationConfig,
    TableProps,
    Tag,
} from "antd";
import Search from "antd/es/input/Search";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const generateHeaderTable = (
  t: TFunction,
  research: () => void,
): TableProps<ContactDetail>["columns"] => {
  return [
    {
      title: t("field.no"),
      dataIndex: "no",
      key: "no",
      align: "center",
      render: (_, _record, index) => <span>{index + 1}</span>,
    },
    {
      title: t("field.firstName"),
      dataIndex: "firstName",
      key: "firstName",
    },
    {
      title: t("field.lastName"),
      dataIndex: "lastName",
      key: "lastName",
    },
    {
      title: t("field.companyName"),
      dataIndex: "companyName",
      key: "companyName",
    },
    {
      title: t("field.departmentName"),
      dataIndex: "departmentName",
      key: "departmentName",
    },
    {
      title: t("field.email"),
      key: "email",
      dataIndex: "email",
    },
    {
      title: t("field.mobile"),
      key: "mobile",
      dataIndex: "mobile",
    },
    {
      title: t("field.inquiryDetail"),
      key: "message",
      dataIndex: "message",
      render: (_, { message }) => (
        <div className={"truncate max-w-[300px]"}>{message}</div>
      ),
    },
    {
      title: t("field.status"),
      key: "stateText",
      dataIndex: "stateText",
      render: (_, { state, stateText }) => (
        <Tag color={state == 0 ? "processing" : "success"}>{stateText}</Tag>
      ),
    },
    {
      title: t("field.submission_time"),
      key: "createdDate",
      dataIndex: "createdDate",
    },
    {
      title: t("field.action"),
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <DetailButton record={record} />
          {record.state == 0 ? (
            <PopconfirmHook
              popconfirmAttrs={{
                title: t("button.change_status"),
                description: t("tip.change_status"),
              }}
              requestApi={() =>
                ContactService.setState({
                  contactId: record.contactId!,
                  state: 1,
                })
              }
              onOk={(res) => {
                toast.success(res?.message || "Success");
                research();
              }}
            >
              {(open) => (
                <Button color="primary" variant="filled" onClick={open}>
                  {t("button.change_status")}
                </Button>
              )}
            </PopconfirmHook>
          ) : null}
        </Space>
      ),
    },
  ];
};

const DetailButton = ({ record }: { record: ContactDetail }) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleCancel = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Button
        color={"primary"}
        variant={"filled"}
        onClick={() => setIsOpen(true)}
      >
        {t("button.detail")}
      </Button>
      <Modal
        title={<h2>{t("title.customerDetail")}</h2>}
        width={999}
        open={isOpen}
        onCancel={handleCancel}
        footer={
          <Button type={"primary"} onClick={handleCancel} size={"large"}>
            {t("button.backList")}
          </Button>
        }
        style={{ top: 40 }}
        styles={{ body: { maxHeight: 685, overflow: "auto" } }}
      >
        <Descriptions
          labelStyle={{
            fontSize: 16,
            color: "#172635",
            fontWeight: 500,
            width: 200,
          }}
          contentStyle={{
            color: "#556476",
            fontSize: 16,
          }}
          colon={false}
          column={1}
          items={[
            {
              key: "firstName",
              label: t("field.firstName"),
              children: record.firstName,
            },
            {
              key: "lastName",
              label: t("field.lastName"),
              children: record.lastName,
            },
            {
              key: "companyName",
              label: t("field.companyName"),
              children: record.companyName,
            },
            {
              key: "departmentName",
              label: t("field.departmentName"),
              children: record.departmentName,
            },
            {
              key: "email",
              label: t("field.email"),
              children: record.email,
            },
            {
              key: "mobile",
              label: t("field.mobile"),
              children: record.mobile,
            },
            {
              key: "message",
              label: t("field.inquiryDetail"),
              children: (
                <div className={"w-full max-h-[409.17px] overflow-x-auto"}>
                  <ContentParagraph content={record.message || ""} />
                </div>
              ),
            },
            {
              key: "state",
              label: t("field.status"),
              children: (
                <Tag color={record.state == 0 ? "processing" : "success"}>
                  {record.stateText}
                </Tag>
              ),
            },
            {
              key: "createdDate",
              label: t("field.submission_time"),
              children: record.createdDate,
            },
          ]}
        />
      </Modal>
    </>
  );
};

const CustomerInquiries = () => {
  const { t } = useTranslation();

  const isFirstRun = useRef<boolean>(true);
  const [dataTable, setDataTable] = useState<ContactDetail[]>([]);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [searchRequest, setSearchRequest] = useState<SearchContactRequest>({
    keyword: "",
    size: 10,
    page: 0,
    sortBy: ["created_date"],
    sortDesc: [true],
  });

  const handleSearch = async () => {
    const res: SearchCustomerResponse | null | undefined =
      await ContactService.search(searchRequest);
    if (!res?.items || res?.items?.length === 0) {
      setTotalRecord(0);
      setDataTable([]);
      if (searchRequest.page > 0) {
        onChangePageNum(1);
        return;
      }
      if (!isFirstRun.current) {
        toast.warning(t("message.noResultsFound"));
      }
      return;
    }
    setTotalRecord(res?.total || 0);
    setDataTable(res?.items);
  };

  const columns: TableProps<ContactDetail>["columns"] = generateHeaderTable(
    t,
    handleSearch,
  );

  const onChangeKeySearch = (value: string) => {
    setSearchRequest((prevState) => ({
      ...prevState,
      page: 0,
      keyword: value,
    }));
  };

  const onChangePageNum = (page: number) => {
    setSearchRequest((prevState) => ({
      ...prevState,
      page: page - 1,
    }));
  };

  const onChangePageSize = (pageSize: number) => {
    setSearchRequest((prevState) => ({
      ...prevState,
      page: 0,
      size: pageSize,
    }));
  };

  const onChangeTable = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ContactDetail> | SorterResult<ContactDetail>[],
  ) => {
    const sortValue = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!sortValue.column) {
      setSearchRequest((prevState) => ({
        ...prevState,
        page: 0,
        sortBy: ["created_date"],
        sortDesc: [true],
      }));
    } else {
      setSearchRequest((prevState) => ({
        ...prevState,
        page: 0,
        sortDesc: [sortValue.order === "descend"],
        sortBy: [StringHelper.toSnakeCase(String(sortValue.columnKey))],
      }));
    }
  };

  useEffect(() => {
    handleSearch().finally(() => {
      if (isFirstRun.current) {
        isFirstRun.current = false;
      }
    });
  }, [searchRequest]);

  return (
    <>
      <ManageContentLayout>
        <div
          className={"p-5 rounded-lg border bg-white"}
          style={{ minHeight: "753px" }}
        >
          {/* Search block */}
          <Flex align="center" justify="space-between">
            <Search
              style={{ width: 555 }}
              placeholder={t("placeholder.search")}
              onSearch={onChangeKeySearch}
              enterButton
              size={"large"}
            />
            <ExportHook
              fileName={`${t("field.customerInquiries")}-${dayjs().format(
                "YYYYMMDDHHmmss",
              )}.csv`}
              requestApi={() => ContactService.export(searchRequest)}
              onOk={(res) => {
                toast.success(res?.message || "Success");
              }}
            >
              {(open, { loading }) => (
                <Button
                  type="primary"
                  loading={loading}
                  icon={<VerticalAlignBottomOutlined />}
                  onClick={open}
                >
                  {t("button.export")}
                </Button>
              )}
            </ExportHook>
          </Flex>
          {/* Table function block */}
          <div className={"mt-5"}>
            <Table
              rowKey={"contactId"}
              columns={columns}
              dataSource={dataTable}
              pagination={false}
              className="overflow-x-auto"
              onChange={onChangeTable}
            />
            <AppPagination
              className={"mt-3"}
              pageSize={searchRequest.size}
              onChangePageSize={onChangePageSize}
              onChangePage={onChangePageNum}
              currentPage={searchRequest.page}
              total={totalRecord}
            />
          </div>
        </div>
      </ManageContentLayout>
    </>
  );
};

export default CustomerInquiries;
